import { Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

import { Day, DayForm, OPEN_STATUS, OpenHoursArray, OpeningTimes } from '@app/api/models/openingTimes.models';
import { defaultOpeningHoursValues } from './open-hours';
import { IOpenHoursService } from '@app/api/interfaces/openHours/i.openHours.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'open-hours',
  templateUrl: 'open-hours.component.html',
  styleUrls: ['./open-hours.component.scss'],
  standalone: false,
})
export class OpenHoursComponent implements OnInit, OnChanges {
  openHoursFormGroup: FormGroup;
  specialHoursFormGroup: FormGroup;

  hours;
  specialDaysHours;
  translationKeyNone;
  currentInitialDate;
  showInitialSpecialTimes = false;
  showSecondPeriod = {
    'csa.monday': { showSecondTime: false },
    'csa.tuesday': { showSecondTime: false },
    'csa.wednesday': { showSecondTime: false },
    'csa.thursday': { showSecondTime: false },
    'csa.friday': { showSecondTime: false },
    'csa.saturday': { showSecondTime: false },
    'csa.sunday': { showSecondTime: false },
  };
  showSecondPeriodSpecialTimes = [{ showSecondTime: false }];
  currentTempClosedValue;

  @Input() showSubTitle;
  @Input() businessOpenTimes;
  @Input() businessSpecialOpenTimes;
  @Input() discardChanges;
  @Input() temporarilyClosed;
  @Output() storeOpeningTimesEmitter = new EventEmitter<undefined>();

  @Output() resetedHours = new EventEmitter();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly openHoursService: IOpenHoursService,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService
  ) {
    this.translationKeyNone = this.showSubTitle ? 'csa.None' : 'csa.none';
  }

  daysSelected: any[] = [];
  event: any;

  ngOnInit() {
    this.buildOpenHoursForm();
    this.buildSpecialHoursForm();
    this.openHoursService.setOpenHours(this.openHoursFormGroup.get('openHours'));

    this.openHoursService.setSpecialOpenHours(this.specialHoursFormGroup.get('specialOpenHours'));

    this.openHoursFormGroup.valueChanges.subscribe(data => {
      this.openHoursService.setOpenHours(data);
      this.storeOpeningTimesEmitter.emit();
    });
    this.specialHoursFormGroup.valueChanges.subscribe(data => {
      this.openHoursService.setSpecialOpenHours(data);
      this.storeOpeningTimesEmitter.emit();
    });

    this.translationKeyNone = this.showSubTitle ? 'csa.None' : 'csa.none';
    this.currentInitialDate = new Date();
    this.openHoursService.setTempClosed(this.temporarilyClosed);
    this.currentTempClosedValue = this.temporarilyClosed !== OPEN_STATUS.OPEN;
  }

  ngOnChanges() {
    if (this.discardChanges) {
      this.resetOpenHours();
    }
  }

  get openHoursArray(): FormArray {
    return this.openHoursFormGroup.get('openHours') as FormArray;
  }

  toggleTempClosed(event) {
    const { checked } = event;
    let tempClosedValue = OPEN_STATUS.OPEN;
    if (checked) {
      tempClosedValue = OPEN_STATUS.CLOSED_TEMPORARILY;
      this.snackBar.open(this.translate.instant('csa.temporarily.closed.mouseover'), '', {
        duration: 4000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'toast-info',
      });
    }
    this.currentTempClosedValue = checked;
    this.openHoursService.setTempClosed(tempClosedValue);
  }

  resetOpenHours() {
    this.buildOpenHoursForm();
    this.openHoursService.setOpenHours(this.openHoursFormGroup.get('openHours'));

    this.buildSpecialHoursForm();
    this.daysSelected = [];
    this.openHoursService.setSpecialOpenHours(this.specialHoursFormGroup.get('specialOpenHours'));

    this.currentTempClosedValue = this.temporarilyClosed !== OPEN_STATUS.OPEN;
    this.openHoursService.setTempClosed(this.temporarilyClosed);

    this.resetedHours.emit(true);
  }

  toggleCheckbox(event, currentForm) {
    const { isSelected, name } = currentForm.controls;
    const { startTime, endTime } = defaultOpeningHoursValues;

    if (!event.checked) {
      currentForm.disable();
      isSelected.enable();
      name.enable();
      this.showSecondPeriod[name.value].showSecondTime = false;
    } else {
      currentForm.controls.from.enable();
      currentForm.controls.to.enable();
      currentForm.controls.splitedFrom.enable();
      currentForm.controls.from.setValue(startTime);
      currentForm.controls.to.setValue(endTime);
      currentForm.controls.splitedFrom.setValue('');
      currentForm.controls.splitedTo.setValue('');
      this.reDefineHoursArray(name.value, 'splitedFrom', endTime);
    }
  }

  selectInitialHour(event, currentSelected, field) {
    const { controls } = currentSelected;
    const { name } = controls;
    this.reDefineHoursArray(name.value, field, event.value);
    controls[field].enable();
  }

  enableHoursDropdown(event, currentSelected, field) {
    const { controls } = currentSelected;
    controls[field].enable();
  }

  showTimePeriod(currentForm) {
    const { name } = currentForm.controls;
    this.showSecondPeriod[name.value].showSecondTime = true;
    currentForm.controls.splitedFrom.enable();
    currentForm.controls.splitedTo.enable();
  }

  deleteTimePeriod(position) {
    const { name } = position.controls;
    this.showSecondPeriod[name.value].showSecondTime = false;
    position.controls.splitedFrom.setValue('');
    position.controls.splitedTo.setValue('');
    position.controls.splitedTo.disable();

    this.reDefineHoursArray(name.value, 'splitedFrom', position.controls.to.value);
  }

  /**
   * Initializes openeing hours with initial state or not if days is not null.
   * @param days days of the week to build
   */
  private buildOpenHoursArray(days?: OpeningTimes) {
    const groups = [];
    if (!days) {
      groups.push(this.buildDefaultOpeningDays('csa.monday'));
      groups.push(this.buildDefaultOpeningDays('csa.tuesday'));
      groups.push(this.buildDefaultOpeningDays('csa.wednesday'));
      groups.push(this.buildDefaultOpeningDays('csa.thursday'));
      groups.push(this.buildDefaultOpeningDays('csa.friday'));
      groups.push(this.buildDefaultOpeningDays('csa.saturday'));
      groups.push(this.buildDefaultOpeningDays('csa.sunday'));
    } else {
      groups.push(this.buildOpenDaysFormControl(days.monday || [], 'csa.monday'));
      groups.push(this.buildOpenDaysFormControl(days.tuesday || [], 'csa.tuesday'));
      groups.push(this.buildOpenDaysFormControl(days.wednesday || [], 'csa.wednesday'));
      groups.push(this.buildOpenDaysFormControl(days.thursday || [], 'csa.thursday'));
      groups.push(this.buildOpenDaysFormControl(days.friday || [], 'csa.friday'));
      groups.push(this.buildOpenDaysFormControl(days.saturday || [], 'csa.saturday'));
      groups.push(this.buildOpenDaysFormControl(days.sunday || [], 'csa.sunday'));
    }

    return groups;
  }
  private reDefineHoursArray(day, field, hour) {
    const allHours = OpenHoursArray();
    const currentPosition = allHours.findIndex(x => x.value === hour);
    this.hours[day][field] = allHours.splice(currentPosition, allHours.length - 1);
  }
  /**
   * Build default opening hours data on the form.
   * @param dayName Description of the day
   */
  private buildDefaultOpeningDays(dayName: string) {
    const { startTime, endTime } = defaultOpeningHoursValues;
    this.reDefineHoursArray(dayName, 'splitedFrom', endTime);
    return this.formBuilder.group(
      {
        name: [dayName],
        isSelected: [true],
        from: [{ value: startTime, disabled: false }],
        to: [{ value: endTime, disabled: false }],
        isSplitService: false,
        splitedFrom: [{ value: '', disabled: false }],
        splitedTo: [{ value: '', disabled: true }],
      },
      { validator: this.openDaysValidator }
    );
  }

  /**
   * Build opening hours data on the form.
   * @param day Day opening hours data
   * @param dayName Description of the day
   */
  private buildOpenDaysFormControl(day: Day[], dayName: string) {
    if (day !== undefined && day.length <= 0) {
      const { endTime } = defaultOpeningHoursValues;
      this.reDefineHoursArray(dayName, 'splitedFrom', endTime);

      return this.formBuilder.group(
        {
          name: [dayName],
          isSelected: [false],
          from: [{ value: '', disabled: true }],
          to: [{ value: '', disabled: true }],
          isSplitService: false,
          splitedFrom: [{ value: '', disabled: false }],
          splitedTo: [{ value: '', disabled: true }],
        },
        { validator: this.openDaysValidator }
      );
    } else if (day !== undefined && day.length > 0) {
      const hasSecondPeriod = day.length > 1;
      this.reDefineHoursArray(dayName, 'splitedFrom', day[0].endTime);
      if (hasSecondPeriod) {
        this.showSecondPeriod[dayName].showSecondTime = true;
      }
      return this.formBuilder.group(
        {
          name: [dayName],
          isSelected: [true],
          from: [{ value: day[0].startTime, disabled: false }],
          to: [{ value: day[0].endTime, disabled: false }],
          isSplitService: hasSecondPeriod,
          splitedFrom: hasSecondPeriod ? [{ value: day[1].startTime, disabled: false }] : [{ value: '', disabled: true }],
          splitedTo: hasSecondPeriod ? [{ value: day[1].endTime, disabled: false }] : [{ value: '', disabled: true }],
        },
        { validator: this.openDaysValidator }
      );
    }
  }

  private buildOpenHoursForm() {
    this.hours = {
      'csa.monday': this.buildHoursArrayPerDay(),
      'csa.tuesday': this.buildHoursArrayPerDay(),
      'csa.wednesday': this.buildHoursArrayPerDay(),
      'csa.thursday': this.buildHoursArrayPerDay(),
      'csa.friday': this.buildHoursArrayPerDay(),
      'csa.saturday': this.buildHoursArrayPerDay(),
      'csa.sunday': this.buildHoursArrayPerDay(),
    };
    const openingHoursToFill = this.businessOpenTimes.length !== 0 ? this.buildOpenHoursArray(this.businessOpenTimes) : this.buildOpenHoursArray();
    this.openHoursFormGroup = this.formBuilder.group({ openHours: this.formBuilder.array(openingHoursToFill) });
  }

  private buildHoursArrayPerDay(): DayForm {
    return { from: OpenHoursArray(), to: OpenHoursArray(), splitedFrom: OpenHoursArray(), splitedTo: OpenHoursArray() };
  }

  /** Special Opening Times Functions */
  isSelected = (event: any) => {
    const date = this.buildDateFormat(event);
    return this.daysSelected.find(x => x === date) ? 'selected' : null;
  };

  select(event: any, calendar: any, specialDayIndex, position) {
    const date = this.buildDateFormat(event);
    const startDate = position.controls.fromDate;

    const index = this.daysSelected.findIndex(x => x === date);
    if (index < 0) {
      if (this.daysSelected.length < 2) {
        let smallDate = date;
        let bigDate = '';
        this.daysSelected.push(date);
        if (this.daysSelected.length === 2) {
          const isDateBigger = this.compareDates(this.daysSelected);
          smallDate = isDateBigger < 0 ? this.daysSelected[0] : this.daysSelected[1];
          bigDate = isDateBigger < 0 ? this.daysSelected[1] : this.daysSelected[0];
        }
        position.controls.fromDate.setValue(smallDate);
        position.controls.toDate.setValue(bigDate);
      }
    } else {
      this.daysSelected.splice(index, 1);
      date === startDate.value ? position.controls.fromDate.setValue('') : position.controls.toDate.setValue('');
    }
    calendar.updateTodaysDate();
  }

  selectInitialSpecialHour(event, currentSelected, index, field) {
    const { controls } = currentSelected;
    this.reDefineSpecialHoursArray(index, field, event.value);
    controls[field].enable();
  }

  buildDefaultSpecialOpenHoursArray(currentSpecialTimes = null) {
    const groups = [];

    if (currentSpecialTimes) {
      const { isClosed, startDate, endDate, closeTime, openTime } = currentSpecialTimes[0];

      const isSplit = currentSpecialTimes.length > 1;
      this.showSecondPeriodSpecialTimes[0].showSecondTime = isSplit;
      groups.push(
        this.formBuilder.group(
          {
            isSelected: [!isClosed],
            fromDate: [{ value: this.changeDateFormat(startDate), disabled: false }],
            toDate: [{ value: this.changeDateFormat(endDate), disabled: false }],
            from: [{ value: openTime || '', disabled: false }],
            to: [{ value: closeTime || '', disabled: false }],
            isSplitService: false,
            splitedFrom: [{ value: isSplit ? currentSpecialTimes[1].openTime : '', disabled: false }],
            splitedTo: [{ value: isSplit ? currentSpecialTimes[1].closeTime : '', disabled: !isSplit }],
          },
          { validator: this.specialDaysValidator }
        )
      );
      this.showInitialSpecialTimes = true;
    } else {
      groups.push(
        this.formBuilder.group(
          {
            isSelected: [false],
            fromDate: [{ value: '', disabled: false }],
            toDate: [{ value: '', disabled: false }],
            from: [{ value: '', disabled: false }],
            to: [{ value: '', disabled: false }],
            isSplitService: false,
            splitedFrom: [{ value: '', disabled: false }],
            splitedTo: [{ value: '', disabled: true }],
          },
          { validator: this.specialDaysValidator }
        )
      );
    }

    return groups;
  }

  toggleSpecialDay(event, currentForm, index) {
    const { isSelected } = currentForm.controls;
    const { startTime, endTime } = defaultOpeningHoursValues;

    if (!event.checked) {
      isSelected.enable();
      this.showSecondPeriodSpecialTimes[index].showSecondTime = false;
      this.daysSelected = [];
    } else {
      currentForm.controls.from.enable();
      currentForm.controls.to.enable();
      currentForm.controls.splitedFrom.enable();
      currentForm.controls.from.setValue(startTime);
      currentForm.controls.to.setValue(endTime);
      currentForm.controls.splitedFrom.setValue('');
      currentForm.controls.splitedTo.setValue('');
      this.reDefineSpecialHoursArray(index, 'splitedFrom', endTime);
    }
  }

  onClickIntitalSpecialTimes() {
    this.showInitialSpecialTimes = true;
  }

  showSecondPeriodSpecialDates(dayPosition) {
    this.showSecondPeriodSpecialTimes[dayPosition].showSecondTime = true;
  }

  deleteTimePeriodSpecialTimes(dayPosition, currentForm) {
    currentForm.controls.splitedTo.setValue('');
    currentForm.controls.splitedFrom.setValue('');
    this.showSecondPeriodSpecialTimes[dayPosition].showSecondTime = false;
  }

  deleteSpecialHourEntry(index, currentForm) {
    this.showInitialSpecialTimes = false;
    this.buildSpecialHoursForm(true);
    this.deleteTimePeriodSpecialTimes(index, currentForm);
    this.daysSelected = [];
    this.openHoursService.setSpecialOpenHours(this.specialHoursFormGroup.get('specialOpenHours'));
  }

  private buildSpecialHoursForm(reset = null) {
    let specialOpenHoursToFill;
    this.specialDaysHours = { 0: this.buildHoursArrayPerDay() };
    if (reset) {
      specialOpenHoursToFill = this.buildDefaultSpecialOpenHoursArray();
    } else {
      specialOpenHoursToFill =
        this.businessSpecialOpenTimes.length > 0
          ? this.buildDefaultSpecialOpenHoursArray(this.businessSpecialOpenTimes)
          : this.buildDefaultSpecialOpenHoursArray();
    }
    this.specialHoursFormGroup = this.formBuilder.group({ specialOpenHours: this.formBuilder.array(specialOpenHoursToFill) });
  }

  private changeDateFormat(date: string): string {
    if (!date || date === '') {
      return '';
    } else {
      const splittedDate = date.split('-');
      const fdate = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`;
      this.daysSelected.push(fdate);
      return fdate;
    }
  }

  private compareDates(dates) {
    const datesToCompare = dates.map(d => {
      const dateArray = d.split('-');
      return new Date(dateArray[2], dateArray[1], dateArray[0]);
    });
    const result = datesToCompare[0].getTime() - datesToCompare[1].getTime();
    return result;
  }

  private buildDateFormat(dateEvent: any) {
    const { year, month, date } = dateEvent._i;
    const _year = year;
    const _month = month + 1 < 10 ? `0${month + 1}` : month + 1;
    const _day = date < 10 ? `0${date}` : date;

    return `${_day}-${_month}-${_year}`;
  }

  private reDefineSpecialHoursArray(index, field, hour) {
    const allHours = OpenHoursArray();
    const currentPosition = allHours.findIndex(x => x.value === hour);
    this.specialDaysHours[index][field] = allHours.splice(currentPosition, allHours.length - 1);
  }

  get specialOpenHoursArray(): FormArray {
    return this.specialHoursFormGroup.get('specialOpenHours') as FormArray;
  }

  /**
   * Custom validator to check every days schedule and see if the hours are
   * set when the day is chosen and to verify if there is overlapping on the hours.
   * @param openDayForm form for each day to be validated
   */
  private readonly openDaysValidator = (openDayForm: FormGroup): Record<string, boolean> => {
    if (openDayForm.controls && openDayForm.controls.isSelected.value) {
      const from = openDayForm.controls.from.value ? parseInt(openDayForm.controls.from.value, 10) : null;
      const to = openDayForm.controls.to.value ? parseInt(openDayForm.controls.to.value, 10) : null;
      const splitedFrom = openDayForm.controls.splitedFrom.value ? parseInt(openDayForm.controls.splitedFrom.value, 10) : null;
      let splitedTo = openDayForm.controls.splitedTo.value ? parseInt(openDayForm.controls.splitedTo.value, 10) : null;
      splitedTo = splitedTo === 0 ? 24 : splitedTo;

      if (from === null) {
        return { fromIsRequired: true, openDaysError: true };
      }

      if (to === null) {
        return { toIsRequired: true, openDaysError: true };
      }

      if (splitedTo !== null && splitedFrom === null) {
        return { splitedFromIsRequired: true, openDaysError: true };
      }

      if (splitedTo === null && splitedFrom !== null) {
        return { splitedToIsRequired: true, openDaysError: true };
      }

      if (splitedTo !== null && splitedFrom !== null) {
        const fromMinutes = timeInMinutes(openDayForm.controls.from.value);
        const toMinutes = timeInMinutes(openDayForm.controls.to.value);
        const splitFrom = timeInMinutes(openDayForm.controls.splitedFrom.value);
        const splitTo = timeInMinutes(openDayForm.controls.splitedTo.value);

        /** Validation to aggregate overlapping hours*/
        if (splitTo && splitFrom === toMinutes) {
          const day = openDayForm.controls.name.value;

          openDayForm.controls.to.setValue(openDayForm.controls.splitedTo.value);
          this.showSecondPeriod[day].showSecondTime = false;
          openDayForm.controls.splitedFrom.setValue('');
          openDayForm.controls.splitedTo.setValue('');
          openDayForm.controls.splitedTo.disable();

          this.reDefineHoursArray(day, 'splitedFrom', openDayForm.controls.to.value);
        }

        /** Validation  of 24h exceeded */
        if (splitTo < splitFrom && !(splitTo <= fromMinutes)) {
          return { splitedToHourOverlapping: true, openDaysError: true };
        }
      }
    }
    return null;
  };

  private readonly specialDaysValidator = (openDayForm: FormGroup): Record<string, boolean> => {
    if (openDayForm.controls && openDayForm.controls.isSelected.value) {
      const from = openDayForm.controls.from.value ? parseInt(openDayForm.controls.from.value, 10) : null;
      const to = openDayForm.controls.to.value ? parseInt(openDayForm.controls.to.value, 10) : null;
      const splitedFrom = openDayForm.controls.splitedFrom.value ? parseInt(openDayForm.controls.splitedFrom.value, 10) : null;
      let splitedTo = openDayForm.controls.splitedTo.value ? parseInt(openDayForm.controls.splitedTo.value, 10) : null;
      splitedTo = splitedTo === 0 ? 24 : splitedTo;

      if (from === null) {
        return { fromIsRequired: true, specialOpenDaysError: true };
      }

      if (to === null) {
        return { toIsRequired: true, specialOpenDaysError: true };
      }

      if (splitedTo !== null && splitedFrom === null) {
        return { splitedFromIsRequired: true, specialOpenDaysError: true };
      }

      if (splitedTo === null && splitedFrom !== null) {
        return { splitedToIsRequired: true, specialOpenDaysError: true };
      }

      if (splitedTo !== null && splitedFrom !== null) {
        const fromMinutes = timeInMinutes(openDayForm.controls.from.value);
        const toMinutes = timeInMinutes(openDayForm.controls.to.value);
        const splitFrom = timeInMinutes(openDayForm.controls.splitedFrom.value);
        const splitTo = timeInMinutes(openDayForm.controls.splitedTo.value);

        /** Validation to aggregate overlapping hours*/
        if (splitTo && splitFrom === toMinutes) {
          openDayForm.controls.to.setValue(openDayForm.controls.splitedTo.value);
          this.showSecondPeriodSpecialTimes[0].showSecondTime = false;
          openDayForm.controls.splitedFrom.setValue('');
          openDayForm.controls.splitedTo.setValue('');
          openDayForm.controls.splitedTo.disable();

          this.reDefineSpecialHoursArray(0, 'splitedFrom', openDayForm.controls.to.value);
        }

        /** Validation  of 24h exceeded */
        if (splitTo < splitFrom && !(splitTo <= fromMinutes)) {
          return { splitedToHourOverlapping: true, specialOpenDaysError: true };
        }
      }
    }
    return null;
  };
}

function timeInMinutes(time) {
  if (time === undefined || time === null) {
    return null;
  }
  const split = time.split(':');
  const hours = Number(split[0]) * 60;
  const minutes = Number(split[1]);
  return hours + minutes;
}
